import { useRef, useState } from 'react';
import { Alert, App, Button, Checkbox, Modal, Space } from 'antd';
import { Plus, UploadSimple } from '@phosphor-icons/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ApiError } from '../../api/client';
import { PageBody, PageHeader } from '../../app/AppLayout';
import { HintBanner, HintToggle, useDismissibleHint } from '../../components/DismissibleHint';
import { ErrorState, LoadingState } from '../../components/PageStates';
import { NetworkError } from '../../api/client';
import { tokens } from '../../theme';
import {
  tierApi,
  tierKeys,
  type CreateTierDraftPayload,
  type TierDefinition,
  type TierImpactReport,
  type UpdateTierDraftPayload,
} from './api';
import { ImpactPreview, TierFormPanel, type TierFormPanelHandle } from './TierFormPanel';

export function TiersPage() {
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  const panelRef = useRef<TierFormPanelHandle>(null);
  const earningHint = useDismissibleHint('tiers-earning-link');
  const [editing, setEditing] = useState<TierDefinition | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  // Đổi mỗi lần bấm "Hủy" — ép Form trong TierFormPanel mount lại (qua `key` bên dưới) để trả
  // về đúng giá trị đã lưu, kể cả khi bản đang hiện không đổi (xem cùng lý do ở EarningRulesPage).
  const [formResetToken, setFormResetToken] = useState(0);

  const [impactPreview, setImpactPreview] = useState<TierImpactReport | null>(null);
  const [impactLoadingId, setImpactLoadingId] = useState<string | null>(null);
  const [publishTarget, setPublishTarget] = useState<TierDefinition | null>(null);
  const [publishReport, setPublishReport] = useState<TierImpactReport | null>(null);
  const [publishAcknowledged, setPublishAcknowledged] = useState(false);
  const [publishForceAck, setPublishForceAck] = useState(false);

  const versionsQuery = useQuery({
    queryKey: tierKeys.all,
    queryFn: tierApi.list,
  });

  const versions = versionsQuery.data ?? [];
  const drafts = versions.filter((v) => !v.isPublished);
  const currentlyEffective = versions.find((v) => v.isCurrentlyEffective);

  // Mặc định hiện BẢN NHÁP mới nhất nếu có (giống prototype a-tier), không thì bản đang áp
  // dụng (chỉ xem), không thì rỗng — cùng quy tắc đã dùng ở EarningRulesPage.
  const displayedVersion = isCreating ? null : (editing ?? drafts[0] ?? currentlyEffective ?? null);

  const saveMutation = useMutation({
    mutationFn: (payload: { id?: string; body: CreateTierDraftPayload | UpdateTierDraftPayload }) =>
      payload.id
        ? tierApi.updateDraft(payload.id, payload.body as UpdateTierDraftPayload)
        : tierApi.createDraft(payload.body as CreateTierDraftPayload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: tierKeys.all });
      message.success('Đã lưu bản nháp.');
      setEditing(null);
      setIsCreating(false);
    },
    onError: (err: unknown) => {
      message.error(err instanceof ApiError ? err.message : 'Không lưu được bản nháp.');
    },
  });

  const publishMutation = useMutation({
    mutationFn: ({ id, acknowledgeImpact }: { id: string; acknowledgeImpact: boolean }) =>
      tierApi.publish(id, acknowledgeImpact),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: tierKeys.all });
      message.success('Đã phát hành thang hạng.');
      setImpactPreview(null);
      setEditing(null);
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError && err.code === 'VALIDATION_FAILED') {
        message.warning(err.message);
      } else {
        message.error(err instanceof ApiError ? err.message : 'Không phát hành được.');
      }
    },
  });

  const openPublishModal = async (version: TierDefinition, forceAck = false) => {
    setImpactLoadingId(version.id);
    try {
      const report = await tierApi.previewImpact(version.id);
      setPublishTarget(version);
      setPublishReport(report);
      setPublishAcknowledged(false);
      setPublishForceAck(forceAck);
    } catch (err) {
      message.error(
        err instanceof ApiError || err instanceof NetworkError
          ? err.message
          : 'Không tải được báo cáo ảnh hưởng.',
      );
    } finally {
      setImpactLoadingId(null);
    }
  };

  const closePublishModal = () => {
    setPublishTarget(null);
    setPublishReport(null);
    setPublishAcknowledged(false);
    setPublishForceAck(false);
  };

  const handlePublishConfirm = async () => {
    if (!publishTarget || !publishReport) return;

    const needsAck = publishForceAck || publishReport.needsAcknowledgement;
    if (needsAck && !publishAcknowledged) {
      message.warning('Cần xác nhận ảnh hưởng trước khi phát hành.');
      return;
    }

    try {
      await publishMutation.mutateAsync({ id: publishTarget.id, acknowledgeImpact: needsAck });
      closePublishModal();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'VALIDATION_FAILED') {
        setPublishForceAck(true);
        message.warning(err.message);
      }
    }
  };

  const loadImpactPreview = async (id: string) => {
    setImpactLoadingId(id);
    try {
      const report = await tierApi.previewImpact(id);
      setImpactPreview(report);
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : 'Không tải được báo cáo.');
      setImpactPreview(null);
    } finally {
      setImpactLoadingId(null);
    }
  };

  // Đọc lại đúng logic của TierFormPanel để quyết định nút Hủy/Lưu ở PageHeader — giống
  // prototype đặt chúng trên đầu trang thay vì cuối form (đổi 2026-09-01).
  const readOnly = displayedVersion?.isPublished === true;
  const hasContent = isCreating || displayedVersion !== null;

  return (
    <>
      <PageHeader
        title="Hạng thẻ"
        subtitle="Phiên bản thang hạng · phát hành khóa nội dung · xem trước ảnh hưởng trước khi áp dụng"
        hint={<HintToggle hint={earningHint} />}
        actions={
          hasContent && !readOnly ? (
            <Space>
              <Button
                onClick={() => {
                  setEditing(null);
                  setIsCreating(false);
                  setFormResetToken((t) => t + 1);
                }}
                disabled={saveMutation.isPending}
              >
                Hủy
              </Button>
              <Button type="primary" loading={saveMutation.isPending} onClick={() => panelRef.current?.submit()}>
                Lưu bản nháp
              </Button>
              {displayedVersion && (
                <Button
                  icon={<UploadSimple size={14} aria-hidden />}
                  loading={impactLoadingId === displayedVersion.id && publishMutation.isPending}
                  onClick={() => void openPublishModal(displayedVersion)}
                >
                  Phát hành
                </Button>
              )}
            </Space>
          ) : (
            !hasContent && (
              <Button type="primary" icon={<Plus size={14} aria-hidden />} onClick={() => setIsCreating(true)}>
                Tạo bản nháp
              </Button>
            )
          )
        }
      />
      <PageBody>
        <HintBanner
          hint={earningHint}
          message={
            <>
              {/* Không liệt kê mã hạng ở đây nữa: bản cũ viết cứng "(bronze, silver, gold,
                  diamond)" và đứng im khi chính sách rút còn ba bậc — một dòng hướng dẫn SAI
                  còn tệ hơn không có, vì người đọc tin nó. Mã thật nằm ngay ở bảng bên dưới. */}
              Hệ số tích theo hạng cấu hình tại{' '}
              <Link to="/earning">Quy tắc tích điểm</Link>, khoá theo <strong>mã</strong> hạng ở
              bảng dưới — không phải tên hiển thị.
            </>
          }
        />

        {versionsQuery.isPending && <LoadingState />}

        {versionsQuery.isError && (
          <ErrorState error={versionsQuery.error} onRetry={() => void versionsQuery.refetch()} />
        )}

        {versionsQuery.isSuccess && (
          <>
            {drafts.length > 0 && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: tokens.space.sectionGap }}
                message={`${drafts.length} bản nháp chưa phát hành`}
                description="Bản nháp chưa ảnh hưởng thành viên nào. Phát hành thì nội dung khóa, chỉ còn đổi được màu hạng."
              />
            )}

            <TierFormPanel
              ref={panelRef}
              key={`${displayedVersion?.id ?? 'new'}-${formResetToken}`}
              version={displayedVersion}
              isCreating={isCreating}
              submitting={saveMutation.isPending}
              onSubmit={(values) => {
                if (displayedVersion) {
                  saveMutation.mutate({ id: displayedVersion.id, body: values });
                } else {
                  saveMutation.mutate({ body: values as CreateTierDraftPayload });
                }
              }}
              onPreviewImpact={() => {
                if (displayedVersion) void loadImpactPreview(displayedVersion.id);
              }}
              impactLoading={displayedVersion !== null && impactLoadingId === displayedVersion.id}
              impactPreview={impactPreview}
              versions={versions}
              // Mọi bản đều mang cùng một bảng màu; bản nháp mới chưa có response riêng nên mượn
              // của bản đang hiện, không thì của bản bất kỳ.
              allowedColors={displayedVersion?.allowedColors ?? versions[0]?.allowedColors ?? []}
              selectedId={displayedVersion?.id ?? null}
              onSelectVersion={(v) => {
                setEditing(v);
                setIsCreating(false);
                setImpactPreview(null);
              }}
              onCreateNew={() => {
                setEditing(null);
                setIsCreating(true);
                setImpactPreview(null);
              }}
            />
          </>
        )}
      </PageBody>

      <Modal
        open={publishTarget !== null && publishReport !== null}
        title={publishTarget ? `Phát hành bản ${publishTarget.version}?` : ''}
        width={640}
        okText="Phát hành"
        cancelText="Hủy"
        confirmLoading={publishMutation.isPending}
        okButtonProps={{
          disabled:
            (publishForceAck || publishReport?.needsAcknowledgement === true) &&
            !publishAcknowledged,
        }}
        onCancel={closePublishModal}
        onOk={() => void handlePublishConfirm()}
        destroyOnHidden
      >
        {publishReport && (
          <>
            <ImpactPreview report={publishReport} />
            {(publishForceAck || publishReport.needsAcknowledgement) && (
              <Checkbox
                style={{ marginTop: 16 }}
                checked={publishAcknowledged}
                onChange={(e) => setPublishAcknowledged(e.target.checked)}
              >
                Tôi hiểu ảnh hưởng lên thành viên và xác nhận phát hành
              </Checkbox>
            )}
          </>
        )}
      </Modal>
    </>
  );
}
